import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { haptic } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload, X, Sparkles } from "lucide-react";

const CATEGORIES = [
  { key: "vetements", label: "Vêtements" },
  { key: "chaussures", label: "Chaussures" },
  { key: "sacs", label: "Sacs" },
  { key: "accessoires", label: "Accessoires" },
];
const CONDITIONS = [
  { key: "neuf_etiquette", label: "Neuf avec étiquette" },
  { key: "tres_bon", label: "Très bon" },
  { key: "bon", label: "Bon" },
  { key: "correct", label: "Correct" },
];

const PACKAGE_SIZES = [
  { key: "small", label: "Petit colis", desc: "T-shirt, top, accessoire (< 500 g)" },
  { key: "medium", label: "Colis moyen", desc: "Pull, jean, robe (< 1 kg)" },
  { key: "large", label: "Grand colis", desc: "Manteau, chaussures, sac (< 2 kg)" },
];

export default function SellListing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("edit");

  const [form, setForm] = useState({
    title: "", description: "", category: "vetements", brand: "", size: "",
    color: "", condition: "tres_bon", price: "", images: [], package_size: "small",
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    if (!editId && !localStorage.getItem("photo_guide_seen")) setShowGuide(true);
  }, [editId]);

  const closeGuide = () => {
    localStorage.setItem("photo_guide_seen", "1");
    setShowGuide(false);
  };

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [user, loading, navigate]);

  useEffect(() => {
    if (editId) {
      api.get(`/products/${editId}`).then(r => {
        setForm({
          title: r.data.title, description: r.data.description,
          category: r.data.category, brand: r.data.brand || "",
          size: r.data.size || "", color: r.data.color || "",
          condition: r.data.condition, price: String(r.data.price), package_size: r.data.package_size || "small",
          images: r.data.images || [],
        });
      });
    }
  }, [editId]);

  const upload = async (e) => {
    const files = Array.from(e.target.files);
    if (form.images.length + files.length > 6) { toast.error("6 photos max"); return; }
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(data.path);
      }
      setForm(f => ({ ...f, images: [...f.images, ...paths] }));
      toast.success(`${paths.length} photo(s) ajoutée(s)`);
    } catch { toast.error("Erreur upload"); }
    finally { setUploading(false); }
  };

  const removeImg = (i) => setForm(f => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  const [enhancingIdx, setEnhancingIdx] = useState(null);
  const [aiPreview, setAiPreview] = useState(null);
  const [aiStyleFor, setAiStyleFor] = useState(null);

  const enhance = async (i, style) => {
    setAiStyleFor(null);
    setEnhancingIdx(i);
    toast.info(style === "mannequin" ? "L'IA prépare le rendu porté… (~20 sec)" : "L'IA améliore ta photo… (~15 sec)");
    try {
      const { data } = await api.post("/uploads/enhance", { path: form.images[i], style }, { timeout: 120000 });
      setAiPreview({ index: i, original: form.images[i], enhanced: data.path });
    } catch { toast.error("L'amélioration IA a échoué, réessaye"); }
    finally { setEnhancingIdx(null); }
  };

  const keepEnhanced = () => {
    setForm(f => ({ ...f, images: f.images.map((img, idx) => idx === aiPreview.index ? aiPreview.enhanced : img) }));
    setAiPreview(null);
    toast.success("Photo améliorée ✨");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.images.length === 0) { toast.error("Ajoute au moins une photo"); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) };
      if (editId) {
        await api.patch(`/products/${editId}`, payload);
        toast.success("Annonce mise à jour ✓");
      } else {
        await api.post("/products", payload);
        toast.success("C'est en ligne ⚡");
      }
      navigate("/dashboard");
    } catch { toast.error("Erreur"); }
    finally { setSubmitting(false); }
  };

  if (!user) return <div className="p-20 text-center">Chargement…</div>;

  return (
    <div className="max-w-2xl mx-auto px-4 lg:px-8 py-6 lg:py-10 fade-in has-bottom-nav" data-testid="sell-page">
      <p className="label mb-2">{editId ? "Modifier" : "Nouveau drop"}</p>
      <h1 className="text-3xl lg:text-5xl font-black tracking-tighter mb-8">
        {editId ? "Mettre à jour" : "Sors un article."}
      </h1>

      <form onSubmit={submit} className="space-y-6">
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="label font-bold">Photos (max 6)</p>
            <button type="button" onClick={() => setShowGuide(true)} className="text-xs font-bold underline flex items-center gap-1" data-testid="photo-guide-link">💡 Conseils photo</button>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {form.images.map((img, i) => (
              <div key={i} className="relative aspect-square overflow-hidden border-2 border-black">
                <img src={fileUrl(img)} className="w-full h-full object-cover" alt="" />
                <button type="button" onClick={() => removeImg(i)} className="absolute top-1 right-1 w-6 h-6 bg-white border-2 border-black flex items-center justify-center" data-testid={`remove-img-${i}`}><X size={12}/></button>
                <button type="button" onClick={() => setAiStyleFor(i)} disabled={enhancingIdx !== null}
                  className="absolute bottom-0 left-0 right-0 h-7 bg-black text-white text-[10px] font-bold flex items-center justify-center gap-1 disabled:opacity-60"
                  data-testid={`enhance-img-${i}`}>
                  {enhancingIdx === i ? "IA en cours…" : <><Sparkles size={11}/> Studio IA ✨</>}
                </button>
              </div>
            ))}
            {form.images.length < 6 && (
              <label className="aspect-square flex flex-col items-center justify-center cursor-pointer border-2 border-dashed border-black gap-2 tap-flash" data-testid="upload-image-label">
                {uploading ? <span className="label">...</span> : <><Upload size={20} strokeWidth={2.5}/><span className="label">+ Photo</span></>}
                <input type="file" accept="image/*" className="hidden" multiple onChange={upload} data-testid="upload-input" />
              </label>
            )}
          </div>
        </div>

        <div>
          <label className="label font-bold mb-2 block">Titre</label>
          <input required className="field" placeholder="Veste denim oversize" value={form.title} onChange={e=>setForm({...form, title:e.target.value})} data-testid="title-input" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label font-bold mb-2 block">Catégorie</label>
            <select className="field" value={form.category} onChange={e=>setForm({...form, category:e.target.value})} data-testid="category-select">
              {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label font-bold mb-2 block">État</label>
            <select className="field" value={form.condition} onChange={e=>setForm({...form, condition:e.target.value})} data-testid="condition-select">
              {CONDITIONS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label font-bold mb-2 block">Marque</label>
            <input className="field" value={form.brand} onChange={e=>setForm({...form, brand:e.target.value})} placeholder="Carhartt" data-testid="brand-input" />
          </div>
          <div>
            <label className="label font-bold mb-2 block">Taille</label>
            <input className="field" value={form.size} onChange={e=>setForm({...form, size:e.target.value})} placeholder="M ou 38" data-testid="size-input" />
          </div>
          <div>
            <label className="label font-bold mb-2 block">Couleur</label>
            <input className="field" value={form.color} onChange={e=>setForm({...form, color:e.target.value})} placeholder="Noir" data-testid="color-input" />
          </div>
          <div>
            <label className="label font-bold mb-2 block">Prix (€)</label>
            <input required type="number" step="0.01" min="0.5" className="field" value={form.price} onChange={e=>setForm({...form, price:e.target.value})} data-testid="price-input" placeholder="29" />
          </div>
        </div>

        <div>
          <label className="label font-bold mb-2 block">Taille du colis 📦</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {PACKAGE_SIZES.map(s => (
              <button key={s.key} type="button" onClick={() => setForm({ ...form, package_size: s.key })}
                className={`text-left p-3 border-2 ${form.package_size === s.key ? "border-black bg-[color:var(--color-mint)]" : "border-[color:var(--color-border-soft)] bg-white hover:border-black"}`}
                data-testid={`package-size-${s.key}`}>
                <p className="font-bold text-sm">{s.label}</p>
                <p className="text-xs text-[color:var(--color-text-muted)]">{s.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="label font-bold mb-2 block">Description</label>
          <textarea required rows="5" className="field" placeholder="Coupe, matière, années de port…" value={form.description} onChange={e=>setForm({...form, description:e.target.value})} data-testid="description-input" />
        </div>

        <button className="btn btn-mint w-full" type="submit" disabled={submitting} onClick={() => haptic()} data-testid="publish-btn">
          {submitting ? "Publication…" : editId ? "Mettre à jour" : "Publier ⚡"}
        </button>
      </form>

      {showGuide && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={closeGuide}>
          <div className="bg-white border-2 border-black p-6 max-w-md w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="photo-guide-modal">
            <h3 className="font-black text-xl mb-1">Réussis tes photos 📸</h3>
            <p className="text-sm text-[color:var(--color-text-muted)] mb-4">Les annonces avec de belles photos se vendent jusqu'à 3x plus vite !</p>
            <ul className="space-y-3 text-sm mb-4">
              <li className="flex gap-2"><span>☀️</span><span><strong>Lumière naturelle</strong> : près d'une fenêtre, en journée. Évite le flash et les ombres dures.</span></li>
              <li className="flex gap-2"><span>🧺</span><span><strong>Fond neutre</strong> : mur clair, drap uni ou sol propre — l'article doit être la star.</span></li>
              <li className="flex gap-2"><span>📐</span><span><strong>Cadre bien</strong> : article entier, à plat ou sur cintre, photo droite et nette.</span></li>
              <li className="flex gap-2"><span>🔍</span><span><strong>Montre tout</strong> : plusieurs angles, l'étiquette, et les éventuels défauts (ça inspire confiance).</span></li>
            </ul>
            <div className="p-3 bg-[color:var(--color-mint)] border-2 border-black mb-4">
              <p className="text-sm font-bold">✨ Le secret Fripri : le Studio IA</p>
              <p className="text-xs">Après l'upload, touche "Studio IA ✨" sur ta photo — l'IA la transforme en rendu pro : fond studio propre ou article <strong>porté par un mannequin</strong>, comme les grandes marques !</p>
            </div>
            <button type="button" onClick={closeGuide} className="btn btn-primary w-full" data-testid="photo-guide-close">C'est parti 📸</button>
          </div>
        </div>
      )}

      {aiStyleFor !== null && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setAiStyleFor(null)}>
          <div className="bg-white border-2 border-black p-5 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="ai-style-modal">
            <h3 className="font-black text-lg mb-1">Studio Photo IA ✨</h3>
            <p className="text-sm text-[color:var(--color-text-muted)] mb-4">Choisis ton rendu — tu pourras comparer avant/après et garder l'originale si tu préfères.</p>
            <div className="space-y-2">
              <button type="button" onClick={() => enhance(aiStyleFor, "mannequin")} className="w-full text-left p-4 border-2 border-black bg-[color:var(--color-mint)] hover:opacity-90" data-testid="ai-style-mannequin">
                <p className="font-black">👤 Rendu porté (mannequin)</p>
                <p className="text-xs">Vêtement porté, chaussures aux pieds, sac à l'épaule — rendu pro type grande marque</p>
              </button>
              <button type="button" onClick={() => enhance(aiStyleFor, "studio")} className="w-full text-left p-4 border-2 border-black bg-white hover:border-[color:var(--color-mint)]" data-testid="ai-style-studio">
                <p className="font-black">🎬 Fond studio propre</p>
                <p className="text-xs">Ton article seul, fond gris clair uniforme, lumière professionnelle</p>
              </button>
            </div>
            <button type="button" onClick={() => setAiStyleFor(null)} className="btn btn-ghost w-full mt-3" data-testid="ai-style-cancel">Annuler</button>
          </div>
        </div>
      )}

      {aiPreview && (
        <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" data-testid="ai-preview-modal">
          <div className="bg-white border-2 border-black max-w-lg w-full p-4 space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-black tracking-tight">✨ Avant / Après</h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="label mb-1">Avant</p>
                <img src={fileUrl(aiPreview.original)} className="w-full aspect-square object-cover border-2 border-black" alt="Avant" />
              </div>
              <div>
                <p className="label mb-1">Après</p>
                <img src={fileUrl(aiPreview.enhanced)} className="w-full aspect-square object-cover border-2 border-black" alt="Après" />
              </div>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setAiPreview(null)} className="btn btn-ghost flex-1" data-testid="ai-keep-original">Garder l'originale</button>
              <button type="button" onClick={keepEnhanced} className="btn btn-mint flex-1" data-testid="ai-use-enhanced">Utiliser l'améliorée ✨</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
